import React, { PureComponent } from 'react';
import { ExportToCsv } from 'export-to-csv-fix-source-map';
import * as dates from "@simplybenefits/sb-dates";
import { Button, Text, Theme, ContentBlock } from "@simplybenefits/sb-ui-library";
import LossRatio from './Layouts/LossRatio.js';
import ReportTable from './Layouts/ReportTable.js';
import LTDReport from './Layouts/LTDReport.js';
import { getPdf } from '../../actions/master.js';
import { connect } from "react-redux";
import store from '../../redux/store.js';
import { constants } from '@simplybenefits/sb-utils';

class Report extends PureComponent {
    constructor(props) {
        super(props);
        this.reportRef = React.createRef();
    }

    componentDidMount() {
        this.reportRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    componentDidUpdate(prevProps) {
        if (prevProps.report !== this.props.report) {
            this.reportRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }

    // eslint-disable-next-line require-await
    _downloadCSV = (report, titleExpansionKeys, reportMeta) => {
        // format the rows to show real dates instead of unix timestamps
        const items = report.items.map((item) => {
            if (item['Birth Date']) {
                item['Birth Date'] = dates.getStartOfDay(item['Birth Date'], 'll');
            }

            if (item['Hire Date']) {
                item['Hire Date'] = dates.getStartOfDay(item['Hire Date'], 'll');
            }

            if (item['Date Paid']) {
                item['Date Paid'] = dates.getLocalDisplay(item['Date Paid'], 'll');
            }

            if (item['Paid Date']) {
                item['Paid Date'] = dates.getLocalDisplay(item['Paid Date'], 'll');
            }

            if (item['Member Status']) {
                item['Member Status'] = constants.memberStatusMap[item['Member Status']];
            }

            // Wrap in quotes to prevent excel form converting to number or removing leading zeros ex: (001 or 045,111,212)
            if (item['Flag Codes']) {
                item['Flag Codes'] = `"${ item['Flag Codes'] }"`;
            }

            return item;
        });

        const reportType = report.reportType;
        const reportTitle = report.name ? `${ reportType } ${ report.name } Report` : `${ reportType } Report`;
        let title = reportTitle;

        if (titleExpansionKeys instanceof Object && !Array.isArray(titleExpansionKeys)) {
            const titleExpansionKeysEntries = Object.entries(titleExpansionKeys);
            const titleExpansion = titleExpansionKeysEntries
                .filter(([ key, value ]) => (key in reportMeta) && (reportMeta[key] || value.default))
                .map(([ key, value ]) => {
                    const data = reportMeta[key] ? reportMeta[key] : value.default;
                    const formatted = typeof value.format === 'function' ? value.format(data) : data;
                    return `(${ key }: ${ formatted })`;
                })
                .join(' ');

            title += `/ ${ titleExpansion }`;
        }

        const date = new Date().toLocaleDateString();
        const options = {
            fieldSeparator: ',',
            filename: `${ reportTitle.replace(' ', '-') }-${ date }`,
            quoteStrings: '"',
            decimalSeparator: '.',
            showLabels: true,
            showTitle: reportType !== 'Orion',
            title,
            useTextFile: false,
            useBom: true,
            useKeysAsHeaders: true
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(items);
    };

    _downloadLtdReport = async (report) => { 
        const accountID = store.getState().accounts.currentAccount?.meta?.accountID || null;
        const groupNum = store.getState().accounts.currentAccount?.meta?.groupNum || null;

        await this.props.dispatch(getPdf("LtdReport", "Long-Term-Disability-Report.pdf", { filters: { groupNum: groupNum || report.groupNum }, accountID }));
    };

    _downloadLossRatioReport = async (report) => { 
        await this.props.dispatch(getPdf("LossRatio", "LossRatio-Report.pdf", { divisionFilter: report.divisionFilter, dateRangeFilter: report.dateRangeFilter, accountID: report.accountID }));
    };

    _downloadDemographicAdjustment = (report) => {
        const linkSource = `data:application/pdf;base64,${ report.pdf }`;
        const downloadLink = document.createElement("a");

        downloadLink.href = linkSource;
        downloadLink.download = "Demographic_Adjustment_Report";
        downloadLink.click();
    };

    _renderDownloadLink(report, titleExpansionKeys, reportMeta) {
        if (!report.items || report.items === {} || report.items.length === 0) {
            return null;
        }

        switch (report.reportType) {
            case 'Loss Ratio':
                return (
                    <Button transparent onClick={() => this._downloadLossRatioReport(report)}>
                            Download
                    </Button>
                );

            case 'LTD':
                return (
                    <Button transparent onClick={() => this._downloadLtdReport(report)}>
                            Download
                    </Button>
                );

            case 'Demographic Adjustment':
                return (
                    <Button transparent onClick={() => this._downloadDemographicAdjustment(report)}>
                            Download
                    </Button>
                );

            default:
                return (
                    <Button transparent onClick={() => this._downloadCSV(report, titleExpansionKeys, reportMeta)}>
                        Download
                    </Button>
                );
        }
    }

    _renderReportTable(report) {
        switch (report.reportType) {
            case 'LTD':
                return <LTDReport report={report.items} />;
            case 'Loss Ratio':
                return <LossRatio report={ report.items } />;
            default:
                return <ReportTable report={report} />;
        }
    }

    render() {
        const { report, isReportTableHidden, titleExpansionKeys = {}, reportMeta = {} } = this.props;
        const reportTitle = report.reportType === 'LTD' ? `${ report.reportType } Report` : report.name ? `${ report.reportType } - ${ report.name }` : report.reportType;
        const styles = {
            titleBlock: {
                display: 'flex',
                flex: 1,
                flexDirection: 'row',
                alignItems: 'center'
            }
        };

        return (
            <ContentBlock ref={ this.reportRef } style={{ marginTop: "30px" }}>
                <div style={ styles.titleBlock }>
                    <Text
                        extraLarge
                        bold
                        color={ Theme.colors.mainTextGrey }
                        margin="0 0 20px 0"
                        customStyle={{ flex: 1 }}
                    >
                        { reportTitle }
                    </Text>

                    { this._renderDownloadLink(report, titleExpansionKeys, reportMeta) }
                </div>

                {
                    (isReportTableHidden === true && (!report.items || report.items === {} || report.items.length === 0)) &&
                        <Text large>No Records</Text>
                }

                {
                    isReportTableHidden !== true && // since the premium remittance report is so long we will only show the download button
                        <>
                            {
                                (!report.items || report.items === {} || report.items.length === 0) ?
                                    <Text large>No Records</Text>
                                    :
                                    <React.Fragment>
                                        { this._renderReportTable(report) }
                                    </React.Fragment>
                            }
                        </>
                }
            </ContentBlock>
        );
    }
}


export default connect()(Report);